'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CostCalculatorProps {
  serviceType?: string;
}

const CostCalculator = ({ serviceType = 'residential' }: CostCalculatorProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [areaSize, setAreaSize] = useState<string>('1000');
  const [roomCount, setRoomCount] = useState<string>('2'); // UI-only: does NOT affect cost calculation
  const [qualityLevel, setQualityLevel] = useState<string>('premium');
  const [estimatedCost, setEstimatedCost] = useState<number>(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const area = parseInt(areaSize) || 0;

    const rateMap: Record<string, Record<string, number>> = {
      residential: { standard: 1000, premium: 1200, luxury: 1400 },
      modular: { standard: 1000, premium: 1200, luxury: 1400 },
      renovation: { standard: 1100, premium: 1300, luxury: 1500 },
    };

    const baseRate = rateMap[serviceType]?.[qualityLevel] ?? 0;

    const cost = area * baseRate;
    setEstimatedCost(cost);
  }, [areaSize, qualityLevel, serviceType, isHydrated]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-soft p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/2" />
          <div className="h-24 bg-muted rounded" />
          <div className="h-24 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-soft p-6 lg:p-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="CalculatorIcon" size={24} className="text-primary" />
        </div>
        <h3 className="font-headline text-2xl font-bold text-foreground">
          Cost Calculator
        </h3>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block font-body text-sm font-medium text-foreground mb-2">
            Carpet Area Size (sq. ft.)
          </label>
          <input
            type="number"
            value={areaSize}
            onChange={(e) => setAreaSize(e.target.value)}
            className="w-full px-4 py-3 bg-background border border-border rounded-md font-body text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter area size"
            min="100"
            max="10000"
          />
        </div>

        <div>
          <label className="block font-body text-sm font-medium text-foreground mb-2">
            Number of Rooms
          </label>
          <input
            type="number"
            value={roomCount}
            onChange={(e) => setRoomCount(e.target.value)}
            className="w-full px-4 py-3 bg-background border border-border rounded-md font-body text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter number of rooms"
            min="1"
            max="20"
          />
        </div>

        <div>
          <label className="block font-body text-sm font-medium text-foreground mb-2">
            Quality Level
          </label>
          <div className="grid grid-cols-3 gap-3">
            {['standard', 'premium', 'luxury'].map((level) => (
              <button
                key={level}
                onClick={() => setQualityLevel(level)}
                className={`px-4 py-3 rounded-md font-body text-sm font-medium transition-all duration-200 ${
                  qualityLevel === level
                    ? 'bg-primary text-primary-foreground shadow-soft'
                    : 'bg-background text-foreground hover:bg-muted'
                }`}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-6 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="font-body text-sm text-muted-foreground">Estimated Project Cost:</span>
          </div>
          <div className="text-3xl font-headline font-bold text-primary">
            {formatCurrency(estimatedCost)}
          </div>
          <p className="font-body text-xs text-muted-foreground mt-2">
            *This is an approximate estimate. Final cost may vary based on specific requirements, material selection, and project complexity.
          </p>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            // dispatch global event to open the lead capture popup (if present)
            const ev = new CustomEvent('hayat:open-lead-popup', { detail: { handled: false } });
            window.dispatchEvent(ev);

            // If no listener handled the event (i.e., popup not mounted on this page),
            // fallback to scrolling to the contact form on the page.
            if (!(ev as any).detail || !(ev as any).detail.handled) {
              const el = document.getElementById('contact-form');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // put focus on the first form control if present
                const firstInput = el.querySelector('input, textarea, select') as HTMLElement | null;
                if (firstInput) firstInput.focus();
              } else {
                // Fallback: set hash so navigation still works if element is not on the page
                window.location.hash = '#contact-form';
              }
            }
          }}
          aria-label="Get detailed quote"
          className="w-full inline-block text-center px-6 py-3 bg-black text-white font-body font-semibold rounded-lg shadow-md hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black/30 transition-colors duration-200"
        >
          Get Detailed Quote
        </button>
      </div>
    </div>
  );
};

export default CostCalculator;